import { useNavigate, useParams } from "react-router-dom";
import addressApi from "api/landlord/address";
import { useState, lazy, Suspense, FC, ReactNode } from "react";
import { getErrorMessage } from "utils/error";
import { Button, ButtonSize, ButtonVariant, Show } from "shared/components";
import {
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  Database01,
  Plus,
  Settings02,
  Settings04
} from "untitledui-js-base";
import { Layout } from "providers/Layout";
import { getShortAddressFromAddress } from "shared/addressUtils";
import { DeletePropertyModal } from "../properties/components/DeletePropertyModal";
import { useAuthContext } from "providers/Auth";
import { UnitsTableView } from "shared/app/UnitsTableView/index";
import { ApplicationsTableView } from "shared/app/ApplicationsTableView/index";
import { Loader } from "shared/components/Loader/index";
import { TemplateReviewModal } from "shared/app/VerificationTemplate/modals/TemplateReviewModal";
import { Tabs } from "shared/components/Tabs/index";
import { Property } from "types/landlord/address";
import { Redirect } from "shared/components/Redirect";
import { BreadCrumbs } from "shared/components/BreadCrumbs";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useMedia } from "shared/providers";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { InviteAndCreateModal } from "shared/app/InviteAndCreateModal";
import { cx } from "shared/utils";
import { PartnerFeatures } from "shared/types";

interface PropertyDetailItemProps {
  label: string;
  value: ReactNode;
  labelClassName?: string;
  valueClassName?: string;
  icon?: ReactNode;
}

const PropertyDetailItem: FC<PropertyDetailItemProps> = ({
  label,
  value,
  labelClassName,
  valueClassName,
  icon
}) => {
  const { isMobile } = useMedia();

  if (isMobile && icon)
    return (
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {icon}
          <p
            className={cx(
              "font-medium text-sm text-content-500",
              labelClassName
            )}
          >
            {label}
          </p>
        </div>
        <p
          className={cx("font-medium text-sm text-content-500", valueClassName)}
        >
          {value}
        </p>
      </div>
    );

  return (
    <div>
      <p
        className={cx(
          "m-0 text-sm font-normal text-content-400",
          labelClassName
        )}
      >
        {label}
      </p>
      <p
        className={cx(
          "m-0 text-sm font-medium text-content-1000",
          valueClassName
        )}
      >
        {value}
      </p>
    </div>
  );
};

const PropertySettings = lazy(() => import("./components/PropertySettings"));

export const PropertyDetail = () => {
  const dictionary = useUnitsPropertiesDictionary();
  const { partnerFeatures, type } = useAuthContext();
  const hasPropertyOwner = partnerFeatures.includes(
    PartnerFeatures.PropertyOwner
  );
  const queryClient = useQueryClient();
  const { propertyId } = useParams();
  const navigate = useNavigate();
  const { isMobile } = useMedia();
  const [tab, setActiveTab] = useState<"Units" | "Applications" | "Sites">(
    dictionary.Units as "Units"
  );
  // MobileView
  const [showDetails, setShowDetails] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>();
  const [showDeletePropertyModal, setShowDeletePropertyModal] = useState(false);
  const [showInviteApplicantsModal, setShowInviteApplicantsModal] =
    useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);

  const propertyMeta = useQuery({
    queryKey: ["properties", propertyId],
    queryFn: () => addressApi.getProperty(propertyId || "")
  });

  const deletePropertyMeta = useMutation({
    mutationKey: ["deleteProperty", propertyId],
    mutationFn: () => addressApi.deleteProperty(propertyId || ""),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["properties", propertyId]
      });
      navigate("/properties");
    },
    onError: (err) => {
      toast.error(getErrorMessage(err));
    }
  });

  const { mutate, isPending } = useMutation({
    mutationKey: ["turnOnOffLinks", propertyId],
    mutationFn: (active: boolean) => {
      return addressApi.updateAllLinks(propertyId || "", active);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`/portal/v1/properties/${propertyId}/units/by_name`]
      });
    },
    onError: (err) => {
      toast.error(getErrorMessage(err));
    }
  });

  if (type.isSDK && !type.isBoomScreen) return <Redirect href="/" />;

  if (propertyMeta.isLoading)
    return (
      <Layout>
        <div className="flex items-center justify-center w-full h-full">
          <Loader />
        </div>
      </Layout>
    );

  const property: Property | undefined = propertyMeta.data;
  if (!property || propertyMeta.isError)
    return (
      <Layout>
        <div>
          <h1 className="mb-4 text-lg font-medium">
            Error loading {dictionary.property}.
          </h1>
          <p className="text-base font-medium">{propertyMeta.error?.message}</p>
        </div>
      </Layout>
    );

  const propertyName =
    property.name || getShortAddressFromAddress(property.address) || "";
  return (
    <Layout
      footerClassName="p-4 border-t border-alpha-100"
      footer={
        <Show if={isMobile}>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => setShowInviteApplicantsModal(true)}
              variant={ButtonVariant.PRIMARY}
              className="w-full"
              size={ButtonSize.SM}
              Icon={Plus}
            >
              Invite applicants
            </Button>

            {type.isPMS || type.isPropertyManager ? null : (
              <Button
                size={ButtonSize.SM}
                variant={ButtonVariant.PRIMARY}
                className="w-full"
                onClick={() =>
                  navigate(`/properties/${propertyId}/units/add`, {
                    state: {
                      property
                    }
                  })
                }
                Icon={Plus}
              >
                Add {dictionary.Units}
              </Button>
            )}
          </div>
        </Show>
      }
      headerClassName="py-0"
      header={
        <>
          <div className="flex items-center justify-between border-b border-alpha-40 py-2 pl-3 md:px-6 md:py-3 gap-2 flex-wrap md:flex-nowrap">
            {isMobile ? (
              <Button
                borderLess
                size={ButtonSize.SM}
                Icon={ArrowLeft}
                onClick={() => {
                  navigate(-1);
                }}
              >
                Back
              </Button>
            ) : (
              <BreadCrumbs
                crumbs={[
                  {
                    title: dictionary.Properties,
                    href: "/properties"
                  },
                  {
                    title: propertyName || `Current ${dictionary.property}`,
                    href: `/properties/${propertyId}`
                  }
                ]}
              />
            )}
            <Button
              borderLess
              variant={ButtonVariant.BASE}
              size={ButtonSize.SM}
              className="stroke-content-400 text-content-400 hover:bg-alpha-40"
              onClick={() => setShowSettings(true)}
              Icon={Settings02}
            >
              <p className="hidden md:flex">{dictionary.Property} settings</p>
            </Button>
          </div>
          <div className="flex items-center justify-between border-b border-alpha-40 px-6 py-5 gap-2 flex-wrap md:flex-nowrap">
            <h4 className="shrink-0 text-xl font-bold text-content-1000 break-all max-w-[calc(80vw-200px)]">
              {propertyName || `Current ${dictionary.property}`}
            </h4>
            <Show if={isMobile}>
              <p className="text-content-500 text-xs font-normal">
                {getShortAddressFromAddress(property.address)}
              </p>
            </Show>
            {type.isBoomScreen ? (
              <div className="hidden md:flex w-full ml-auto">
                <Button
                  onClick={() => setShowInviteApplicantsModal(true)}
                  variant={ButtonVariant.PRIMARY}
                  className="ml-auto md:w-auto w-full"
                  size={ButtonSize.SM}
                  Icon={Plus}
                >
                  Invite applicants
                </Button>
              </div>
            ) : null}
          </div>
          <div className="border-b border-alpha-100 flex md:items-center justify-start flex-wrap md:flex-nowrap gap-2 md:gap-12 md:px-6 md:py-4 pb-2">
            <Show if={type.isBoomScreen}>
              {isMobile ? (
                <div className="pl-3 py-2 w-full">
                  <Show if={!showDetails}>
                    <Button
                      borderLess
                      size={ButtonSize.SM}
                      Icon={ChevronDown}
                      onClick={() => setShowDetails(true)}
                    >
                      Show details
                    </Button>
                  </Show>

                  <Show if={showDetails}>
                    <div className="flex flex-col pt-4 gap-3 px-4 w-full">
                      <PropertyDetailItem
                        label="Screen settings"
                        value={property.verification_template?.name ?? "--"}
                        icon={
                          <Settings04
                            className="shrink-0 stroke-content-500"
                            size="20"
                          />
                        }
                      />

                      <Show if={type.isPMS}>
                        <PropertyDetailItem
                          label="Source"
                          value={property.pms_id ? "PMS" : "Manually added"}
                          icon={
                            <Database01
                              className="shrink-0 stroke-content-500"
                              size="20"
                            />
                          }
                        />
                      </Show>

                      <Show if={hasPropertyOwner}>
                        <PropertyDetailItem
                          label="Owner"
                          value={property.owner || "--"}
                        />
                      </Show>

                      <PropertyDetailItem
                        label="Address"
                        value={getShortAddressFromAddress(property.address)}
                      />

                      <PropertyDetailItem
                        label={dictionary.Property + " ID"}
                        value={property.external_id || "--"}
                      />

                      <PropertyDetailItem
                        label="Property group"
                        value={property.property_group?.name || "--"}
                      />
                    </div>
                  </Show>

                  <Show if={showDetails}>
                    <Button
                      borderLess
                      size={ButtonSize.SM}
                      Icon={ChevronUp}
                      className="mt-2"
                      onClick={() => setShowDetails(false)}
                    >
                      Hide details
                    </Button>
                  </Show>
                </div>
              ) : (
                <div className="flex gap-12">
                  <PropertyDetailItem
                    label="Screen settings"
                    value={property.verification_template?.name ?? "--"}
                    labelClassName="text-content-1000"
                  />
                  <PropertyDetailItem
                    label="Address"
                    value={getShortAddressFromAddress(property.address)}
                    valueClassName="break-all max-w-[600px]"
                  />
                  <Show if={type.isPMS}>
                    <PropertyDetailItem
                      label="Source"
                      value={property.pms_id ? "PMS" : "Manually added"}
                    />
                  </Show>
                  <Show if={hasPropertyOwner}>
                    <PropertyDetailItem
                      label="Owner"
                      value={property.owner || "--"}
                    />
                  </Show>
                  <PropertyDetailItem
                    label={dictionary.Property + " ID"}
                    value={property.external_id || "--"}
                  />
                  <PropertyDetailItem
                    label="Property group"
                    value={
                      <p className="text-content-1000 font-medium">
                        {property.property_group?.name || "--"}
                      </p>
                    }
                  />
                </div>
              )}
            </Show>
          </div>
        </>
      }
    >
      <div>
        <Tabs
          selected={tab}
          setActiveTab={(t) => setActiveTab(t as "Units" | "Applications")}
          tabs={
            type.isBoomScreen
              ? [dictionary.Units, "Applications"]
              : [dictionary.Units]
          }
          variant="underline"
        />

        <div className="pt-4">
          {tab === dictionary.Units ? (
            <UnitsTableView
              query={`/portal/v1/properties/${propertyId}/units/by_name`}
              propertyId={Number(propertyId)}
              buttons={
                <div className="flex w-full gap-2 md:w-auto">
                  <Show if={type.isBoomScreen}>
                    <Button
                      size={ButtonSize.SM}
                      variant={ButtonVariant.BASE}
                      onClick={() => mutate(true)}
                      loading={isPending}
                      className="w-full md:w-auto"
                    >
                      Allow all
                    </Button>
                    <Button
                      size={ButtonSize.SM}
                      variant={ButtonVariant.BASE}
                      onClick={() => mutate(false)}
                      loading={isPending}
                      className="w-full md:w-auto"
                    >
                      Disallow all
                    </Button>
                  </Show>
                  {type.isPMS || type.isPropertyManager ? null : (
                    <Button
                      size={ButtonSize.SM}
                      variant={ButtonVariant.PRIMARY}
                      onClick={() =>
                        navigate(`/properties/${propertyId}/units/add`, {
                          state: {
                            property
                          }
                        })
                      }
                      Icon={Plus}
                    >
                      Add {dictionary.Units}
                    </Button>
                  )}
                </div>
              }
            />
          ) : (
            <ApplicationsTableView propertyId={Number(propertyId)} />
          )}
        </div>
      </div>

      <DeletePropertyModal
        opened={showDeletePropertyModal}
        onClose={() => setShowDeletePropertyModal(false)}
        onDelete={() => {
          deletePropertyMeta.mutate();
        }}
      />
      {showInviteApplicantsModal && propertyName && (
        <InviteAndCreateModal
          open={showInviteApplicantsModal}
          setOpen={() => setShowInviteApplicantsModal(false)}
          propertyId={property.id}
          propertyName={propertyName}
          // unitId={undefined}
          onInviteRefreshKey={["properties", propertyId]}
        />
      )}
      <TemplateReviewModal
        opened={showReviewModal}
        onClose={() => setShowReviewModal(false)}
        id={selectedTemplateId}
      />
      <Suspense fallback={null}>
        <PropertySettings
          showSettings={showSettings}
          setShowSettings={setShowSettings}
          property={property}
          refetch={() => {
            propertyMeta.refetch();
          }}
          setShowDeletePropertyModal={setShowDeletePropertyModal}
          selectedTemplateId={selectedTemplateId || ""}
          setSelectedTemplateId={setSelectedTemplateId}
          setShowReviewModal={setShowReviewModal}
        />
      </Suspense>
    </Layout>
  );
};
